/**
 * Triggers the scan-instagram-audio Edge Function for a given ad creative.
 *
 * Behavior:
 * - Calls the Instagram Media API (RapidAPI) to detect audio_type.
 * - If audio_type === 'licensed_music', the backend downloads the video from
 *   Instagram, uploads it to the `videos` storage bucket, and overwrites
 *   `merged_instagram_reel_url` and `content` on the ad_creatives row with the
 *   storage URL. The existing `set_platform_for_direct_uploads` trigger then
 *   flips the platform to `direct_upload`.
 * - If audio_type === 'original_sounds', only the `audio_type` column is set.
 */
export interface ScanInstagramAudioResult {
  creative_id: string;
  instagram_url: string;
  audio_type: 'licensed_music' | 'original_sounds' | null;
  licensed: boolean;
  downloaded: boolean;
  storage_url?: string;
  thumbnail_url?: string;
  error?: string;
}

export async function scanInstagramAudio(
  creativeId: string,
  instagramUrl?: string,
  fetchThumbnailOnly?: boolean
): Promise<ScanInstagramAudioResult | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !anonKey) return null;

    const res = await fetch(`${supabaseUrl}/functions/v1/scan-instagram-audio`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        creative_id: creativeId,
        ...(instagramUrl ? { instagram_url: instagramUrl } : {}),
        ...(fetchThumbnailOnly ? { fetch_thumbnail_only: true } : {}),
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      console.error('scan-instagram-audio failed:', res.status, text);
      return null;
    }
    return (await res.json()) as ScanInstagramAudioResult;
  } catch (err) {
    console.error('scanInstagramAudio error:', err);
    return null;
  }
}

export async function scanAllInstagramAudio(
  limit = 50
): Promise<{
  total: number;
  licensed: number;
  original: number;
  downloaded: number;
  errors: number;
  results: ScanInstagramAudioResult[];
} | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !anonKey) return null;

    const res = await fetch(`${supabaseUrl}/functions/v1/scan-instagram-audio`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ scan_all: true, limit }),
    });

    if (!res.ok) {
      const text = await res.text();
      console.error('scan-instagram-audio bulk failed:', res.status, text);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('scanAllInstagramAudio error:', err);
    return null;
  }
}
